import {
    orderBy as _orderBy,
} from '@lodash';
import { Dispatch, Commit } from 'vuex';
import LottoEvent from '@core/models/lotto/LottoEvent';
import config from '@config';
import { IState } from '@store/index';
import { FilterType } from '@store/modules/ui/lotto/enums';
import { IFilterState } from './types';
import { NO_FILTER } from './common';
import { SET_SELECTED_TREE_ITEMS } from './mutationTypes';

import * as SystemFilter from './systemFilter';
import * as LotteryFilter from './lotteryFilter';
import * as FeaturedFilter from './featuredFilter';
import {
    getTimeFilterFunctionForTimeFilter,
    getTimeFilterFunctionForDate,
} from '@shared/timeFilter/helpers';
import { routeNames } from '@app/router';

function defaultState(): IFilterState {
    return {
        selectedTreeItems: [],
        selectedFeaturedItem: null,
        availableFeaturedItems: config.app.lotto.featuredGames,
    };
}

export default ({
    baseLotteryRoute,
    lotteryFilterRoute,
    lotterySystemRoute,
    eventsGetter,
    selectedEventGetter,
}: {
    baseLotteryRoute: string,
    lotteryFilterRoute: string,
    lotterySystemRoute: string,
    eventsGetter: string,
    selectedEventGetter: string,
}) => ({
    namespaced: true,

    state: defaultState(),

    mutations: {
        ...SystemFilter.mutations,
        ...LotteryFilter.mutations,
        ...FeaturedFilter.mutations,
    },

    getters: {
        ...SystemFilter.getters,
        ...LotteryFilter.getters,
        ...FeaturedFilter.getters,

        /**
         * Returns events that are shown in the grid. All events are first
         * filtered by date and then by whichever additional filter is currently
         * active (by system, by lottery or by featured item).
         */
        lottoEvents(state: IFilterState, getters: any) : LottoEvent[] {
            let predicate = NO_FILTER;

            if (getters.selectedFeaturedItem) {
                predicate = getters.featuredFilter;
            } else if (getters.isLotteryFilter) {
                predicate = getters.lotteryFilter;
            } else if (getters.isSystemFilter) {
                predicate = getters.systemFilter;
            }

            return getters._lottoEventsFilteredByDate.filter(predicate);
        },

        /**
         * Returns true if events are currently filtered by lotteries (this
         * includes the featured lotteries filter), false otherwise.
         */
        isLotteryFilter: (state: IFilterState, getters: any, rootState: IState, rootGetters: any): boolean =>
            [baseLotteryRoute, lotteryFilterRoute].includes(rootGetters['navigation/route'].name),
        /**
         * Returns true if events are currently filtered by systems, false otherwise.
         * NOTE: Even though it can currently be viewed as !isLotteryFilter, it's written explicitly
         * for the sake of clarity and to enable easier changes in the future.
         */
        isSystemFilter: (state: IFilterState, getters: any, rootState: IState, rootGetters: any): boolean =>
            rootGetters['navigation/route'].name === lotterySystemRoute,

        /**
         * Returns events that are filtered by timefilter and sorted for event grid.
         * Events are sorted by date and code.
         */
        _lottoEventsFilteredByDate(state: IFilterState, getters: any, rootState: IState, rootGetters: any): string[] {
            const eventsMap = rootGetters[eventsGetter] as Record<number, LottoEvent>;
            // Do not remove currentTime. It's used to bust the cache of this getter
            const currentTime = rootState.ui.lotto.timestamp;
            const expandedEventId = rootGetters[selectedEventGetter];
            let timeFilter = rootGetters['ui/lotto/timeFilter'];

            if ([routeNames.lottoResults, routeNames.lottoGameResults, routeNames.lottoSystemResults].includes(
                rootGetters['navigation/route'].name
            ) && timeFilter === 'today') {
                timeFilter = new Date();
            }

            const timeFilterFn = timeFilter instanceof Date
                ? getTimeFilterFunctionForDate(timeFilter)
                : getTimeFilterFunctionForTimeFilter(timeFilter);

            const events = Object.values(eventsMap)
                .filter((event) => (timeFilterFn(event) || event.id === expandedEventId));

            return _orderBy(events as any[], ['date', 'code']);
        },
    },

    actions: {
        ...SystemFilter.actions,
        ...LotteryFilter.actions({ lotteryFilterRoute }),
        ...FeaturedFilter.actions,

        changeFilterUrl({ dispatch, rootGetters }: { dispatch: Dispatch, rootGetters: any },
                        filterType: FilterType): void {

            let routeName = '';
            if (filterType === FilterType.lotteries) {
                routeName = lotteryFilterRoute;
            } else {
                routeName = lotterySystemRoute;
            }
            const params = rootGetters['navigation/route'].params;
            dispatch('navigation/push', { params, name: routeName }, { root: true });
            dispatch('resetFilterState', filterType);
        },

        resetFilterState({ commit }: { commit: Commit }, filterType: FilterType): void {
            if (filterType === FilterType.lotteries) {
                commit(SET_SELECTED_TREE_ITEMS, []);
            }
        },
        clearFilterState({ commit }: { commit: Commit }) {
            commit(SET_SELECTED_TREE_ITEMS, []);
        }
    },

});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/shared/lotto/filter/filterIndex.ts